import { useMutation } from '@tanstack/react-query';
import { useRouter } from '@tanstack/react-router';
import { Button, Field, Select, Tabs, Textarea } from '@bb/ui';
import { useState } from 'react';
import { ApiError, type AdminApi, type PayloadPreview, type PreviewProfile, type PushContentInput, type PushPreviewRequest, type PushPreviewResult } from '../../../api/client.ts';
import { HumanView, WarningsView, type PreviewSample } from '../shared/PayloadPreviewPanel.tsx';
import { decodedFcmData, parseJsonObject } from '../shared/personalization.ts';
import { DEFAULT_PREVIEW_SEED, ProfileSelector } from '../shared/ProfileSelector.tsx';
import { environmentLocales } from '../shared/localization.ts';
import { useEnvironmentLocales } from '../shared/useEnvironmentLocales.ts';
import { useTemplateVariables } from '../shared/useTemplateVariables.ts';
import { structuralIssues } from './pushChecks.ts';
import { PLATFORM_LABELS, PUSH_CHANNEL, type PushPlatform } from './pushDraft.ts';

export interface PushTestTabProps {
  environmentId: string;
  /** The campaign: its trigger data is offered as samples. */
  campaignId?: string;
  /** The contents to preview: the shared one, or each platform's own. */
  contents: readonly PushContentInput[];
  /** Trigger data samples to edit as JSON; by default what the campaign's variables may read. */
  samples?: readonly PreviewSample[];
}

const SAMPLE_FIELDS: Record<PreviewSample, { label: string; key: 'eventProperties' | 'apiTriggerProperties' | 'context' }> = {
  event_properties: { label: 'Event properties (JSON)', key: 'eventProperties' },
  api_trigger_properties: { label: 'API trigger properties (JSON)', key: 'apiTriggerProperties' },
  context: { label: 'Canvas context (JSON)', key: 'context' },
};
const SAMPLE_ORDER: readonly PreviewSample[] = ['event_properties', 'api_trigger_properties', 'context'];

function errorText(error: unknown): string {
  if (!(error instanceof ApiError)) return 'The preview could not be rendered.';
  if (error.code === 'profile_not_found') return 'No such user in this environment: check the external ID, or preview a random user.';
  if (error.status === 403) return 'Previewing needs the campaigns.edit permission.';
  return error.message;
}

function PlatformResult({ platform, result }: { platform: PushPlatform; result: PayloadPreview }) {
  const [tab, setTab] = useState('view');
  const channel = PUSH_CHANNEL[platform];
  const warningCount = result.warnings.length + (result.aborted ? 1 : 0);
  const decoded = decodedFcmData(result.payload);
  return (
    <section className="push-test__platform" aria-label={`${PLATFORM_LABELS[platform]} result`}>
      <h4>{PLATFORM_LABELS[platform]}</h4>
      <p className="hint">
        User <code>{result.profileId}</code> in {result.locale}.
      </p>
      <Tabs
        label={`${PLATFORM_LABELS[platform]} preview result`}
        value={tab}
        onValueChange={setTab}
        items={[
          { value: 'view', label: 'View', content: <HumanView channel={channel} result={result} /> },
          {
            value: 'json',
            label: 'Payload JSON',
            content: (
              <>
                <pre className="sdk-log" aria-label={`${PLATFORM_LABELS[platform]} exact payload`}>
                  {JSON.stringify(result.payload, null, 2)}
                </pre>
                {decoded !== undefined && (
                  <details>
                    <summary>data.bb decoded</summary>
                    <pre className="sdk-log">{JSON.stringify(decoded, null, 2)}</pre>
                  </details>
                )}
              </>
            ),
          },
          { value: 'warnings', label: warningCount ? `Warnings (${warningCount})` : 'Warnings', content: <WarningsView result={result} /> },
        ]}
      />
    </section>
  );
}

/**
 * The Test tab of the push composer (BB-73): the content rendered by the delivery code for one user — random with a
 * seed, by external ID, or a test user — with optional trigger data and locale, on both platforms side by side
 * (`POST …/push/preview`): the notification in words, the exact FCM message and APNs request, and the warnings.
 */
export function PushTestTab({ environmentId, campaignId, contents, samples }: PushTestTabProps) {
  const { api } = useRouter().options.context as { api: AdminApi };
  const [profile, setProfile] = useState<PreviewProfile | null>({ kind: 'random', seed: DEFAULT_PREVIEW_SEED });
  const [locale, setLocale] = useState('');
  const [sampleText, setSampleText] = useState<Partial<Record<PreviewSample, string>>>({});
  const locales = useEnvironmentLocales(environmentId);
  const codes = locales ? environmentLocales(locales).codes : [];
  const variables = useTemplateVariables(environmentId, campaignId);
  const shownSamples = samples ?? SAMPLE_ORDER.filter((s) => variables.data?.namespaces.includes(s));
  const parsed = shownSamples.map((s) => ({ sample: s, ...parseJsonObject(sampleText[s] ?? '') }));
  const invalidSample = parsed.some((p) => p.error);
  const structural = contents.flatMap((c) => structuralIssues(c));

  const requests: PushPreviewRequest[] | null =
    profile && !invalidSample && structural.length === 0 && contents.length
      ? contents.map((content) => ({
          content,
          profile,
          ...Object.fromEntries(parsed.flatMap((p) => (p.value ? [[SAMPLE_FIELDS[p.sample].key, p.value]] : []))),
          ...(locale ? { locale } : {}),
        }))
      : null;
  const [result, setResult] = useState<PushPreviewResult>();
  const [shownFor, setShownFor] = useState<string | null>(null);
  const preview = useMutation({
    mutationFn: async (list: PushPreviewRequest[]) => Object.assign({}, ...(await Promise.all(list.map((r) => api.previewPush(environmentId, r))))) as PushPreviewResult,
    onSuccess: setResult,
    onError: () => setResult(undefined),
  });
  const outdated = result && requests && shownFor !== JSON.stringify(requests);
  const run = () => {
    if (!requests) return;
    setShownFor(JSON.stringify(requests));
    preview.mutate(requests);
  };
  const platforms = (['android', 'ios'] as const).filter((p) => result?.[p]);

  return (
    <section className="panel push-test" aria-label="Test">
      <h3>Preview as user</h3>
      <div className="stack-form">
        <ProfileSelector environmentId={environmentId} value={profile} onChange={setProfile} />
        {codes.length > 1 && (
          <Field label="Locale" hint="Another language than the user’s, to check a translation.">
            <Select options={[{ value: '', label: 'The user’s language' }, ...codes.map((c) => ({ value: c, label: c }))]} value={locale} onChange={(e) => setLocale(e.target.value)} />
          </Field>
        )}
        {shownSamples.length > 0 && (
          <fieldset>
            <legend>Trigger data samples</legend>
            {parsed.map((p) => (
              <Field key={p.sample} label={SAMPLE_FIELDS[p.sample].label} error={p.error}>
                <Textarea rows={3} spellCheck={false} value={sampleText[p.sample] ?? ''} placeholder='{"symbol": "EURUSD"}' onChange={(e) => setSampleText((was) => ({ ...was, [p.sample]: e.target.value }))} />
              </Field>
            ))}
          </fieldset>
        )}
        {structural.length > 0 && <p className="hint push-issue--warning">Fix the content first: {structural.length === 1 ? 'one field is' : `${structural.length} fields are`} not valid (see Checks).</p>}
        <div className="toolbar">
          <Button variant="primary" onClick={run} loading={preview.isPending} disabled={!requests}>
            Preview both platforms
          </Button>
        </div>
      </div>
      {preview.isError && <p role="alert">{errorText(preview.error)}</p>}
      {result && !preview.isError && (
        <>
          {outdated && (
            <p className="hint" role="status">
              The message or the choices changed since: preview again to update it.
            </p>
          )}
          <div className="push-test__results" aria-busy={preview.isPending || undefined}>
            {platforms.map((p) => (
              <PlatformResult key={p} platform={p} result={result[p]!} />
            ))}
          </div>
          <p className="hint">Always confirm with a test send on a real device.</p>
        </>
      )}
    </section>
  );
}
